import type { ConfigType } from './index';

type RegionalApiConfig = {
  readonly API: Readonly<Partial<Pick<ConfigType['API'], 'AI' | 'GRAPHQL_BFF' | 'FILES' | 'ACCOUNTS' | 'USERS'>>>;
  readonly DATADOG: {
    readonly RUM: Readonly<Partial<Pick<ConfigType['DATADOG']['RUM'], 'NOVA'>>>;
  };
};

// RUM applications live in the EU Datadog organization (datadoghq.eu). Like the
// US apps, stage and prod share one application and are split by the env tag.
const EU_NOVA_RUM = {
  APPLICATION_ID: 'd80a6b67-73a8-4211-8c70-4c2ce5d800a5',
  CLIENT_TOKEN: 'pub8218608fa8c129b4304f8ebdc06260b7',
};

// EU apps use the regional gateway for browser calls and cluster-local origins
// for server-only FILES/ACCOUNTS/USERS clients. Verify required BFF operations
// before onboarding another app.
// AUTH and KEYCLOAK continue to come from the base config in both environments.
export const europeConfig = {
  prod: {
    API: {
      AI: 'https://kong.prod.eu.newfront.com',
      GRAPHQL_BFF: 'https://kong.prod.eu.newfront.com/api/bff/graphql',
      FILES: 'http://api-files:3000',
      ACCOUNTS: 'http://api-account:3000',
      USERS: 'http://api-users:3000',
    },
    DATADOG: { RUM: { NOVA: EU_NOVA_RUM } },
  },
  stage: {
    API: {
      AI: 'https://kong.stage.eu.newfront-development.com',
      GRAPHQL_BFF: 'https://kong.stage.eu.newfront-development.com/api/bff/graphql',
    },
    DATADOG: { RUM: { NOVA: EU_NOVA_RUM } },
  },
} as const satisfies Record<'prod' | 'stage', RegionalApiConfig>;

/**
 * Whether an app has a RUM application in the EU Datadog organization. Apps
 * without one must not send their US client token to datadoghq.eu.
 */
export function hasEuropeRumApp(appName: keyof ConfigType['DATADOG']['RUM']): boolean {
  return appName in europeConfig.prod.DATADOG.RUM && appName in europeConfig.stage.DATADOG.RUM;
}
